import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { updateUserProfile } from '../lib/database';

const steps = [
  'Personal Information',
  'Partner Information',
  'Retirement Goals',
  'Assets & Liabilities',
  'Cash Flow',
];

const Onboarding = ({ onComplete }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasPartner, setHasPartner] = useState(false);

  // Form data state
  const [formData, setFormData] = useState({
    // Partner Information
    hasPartner: false,
    partner: {
      firstName: '',
      lastName: '',
      dateOfBirth: '',
    },
    
    // Personal Information
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    
    // Retirement Goals
    retirementAge: '',
    partnerRetirementAge: '',
    retirementLivingExpenses: '',
    
    // Assets & Liabilities
    assets: [],
    liabilities: [],
    
    // Cash Flow
    income: [],
    expenses: [],
  });

  // Asset and liability categories
  const assetCategories = [
    { value: 'cash', label: 'Cash' },
    { value: 'investments', label: 'Investments' },
    { value: 'property', label: 'Property' },
    { value: 'superannuation', label: 'Superannuation' },
    { value: 'other', label: 'Other' },
  ];

  const liabilityCategories = [
    { value: 'mortgage', label: 'Mortgage' },
    { value: 'loans', label: 'Loans' },
    { value: 'creditCards', label: 'Credit Cards' },
    { value: 'other', label: 'Other' },
  ];

  const incomeCategories = [
    { value: 'salary', label: 'Salary' },
    { value: 'investments', label: 'Investment Income' },
    { value: 'rental', label: 'Rental Income' },
    { value: 'business', label: 'Business Income' },
    { value: 'other', label: 'Other' },
  ];

  const expenseCategories = [
    { value: 'housing', label: 'Housing' },
    { value: 'utilities', label: 'Utilities' },
    { value: 'transportation', label: 'Transportation' },
    { value: 'food', label: 'Food' },
    { value: 'entertainment', label: 'Entertainment' },
    { value: 'insurance', label: 'Insurance' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'other', label: 'Other' },
  ];

  // New item states
  const [newAsset, setNewAsset] = useState({ value: '', category: '', name: '' });
  const [newLiability, setNewLiability] = useState({ value: '', category: '', name: '' });
  const [newIncome, setNewIncome] = useState({ value: '', category: '', name: '' });
  const [newExpense, setNewExpense] = useState({ value: '', category: '', name: '' });

  const handleInputChange = (section, field, value, ownership = null) => {
    setFormData(prev => {
      if (section === 'partner') {
        return {
          ...prev,
          partner: {
            ...prev.partner,
            [field]: value
          }
        };
      }
      if (section) {
        return {
          ...prev,
          [section]: {
            ...prev[section],
            [field]: {
              ...prev[section][field],
              value,
              ...(ownership !== null && { ownership })
            }
          },
        };
      }
      return {
        ...prev,
        [field]: value,
      };
    });
  };

  const handlePartnerToggle = (event) => {
    setHasPartner(event.target.checked);
    setFormData(prev => ({
      ...prev,
      hasPartner: event.target.checked,
    }));
  };

  const renderOwnershipSelect = (section, field) => {
    const value = formData[section][field].ownership || 'person1';
    const userName = formData.firstName || 'Your Name';
    return (
      <FormControl fullWidth margin="normal">
        <InputLabel>Ownership</InputLabel>
        <Select
          value={value}
          onChange={(e) => handleInputChange(section, field, formData[section][field].value, e.target.value)}
          label="Ownership"
        >
          <MenuItem value="person1">{userName}</MenuItem>
          {hasPartner && <MenuItem value="partner">{formData.partner.firstName || 'Partner'}</MenuItem>}
          {hasPartner && <MenuItem value="joint">Joint</MenuItem>}
        </Select>
      </FormControl>
    );
  };

  // Add new asset or liability
  const handleAddAsset = () => {
    if (newAsset.value && newAsset.category) {
      setFormData(prev => ({
        ...prev,
        assets: [
          ...prev.assets,
          {
            id: Date.now(),
            name: newAsset.name || assetCategories.find(cat => cat.value === newAsset.category)?.label || 'Unnamed Asset',
            value: newAsset.value,
            category: newAsset.category,
            ownership: 'person1'
          }
        ]
      }));
      setNewAsset({ value: '', category: '', name: '' });
    }
  };

  const handleAddLiability = () => {
    if (newLiability.value && newLiability.category) {
      setFormData(prev => ({
        ...prev,
        liabilities: [
          ...prev.liabilities,
          {
            id: Date.now(),
            name: newLiability.name || liabilityCategories.find(cat => cat.value === newLiability.category)?.label || 'Unnamed Liability',
            value: newLiability.value,
            category: newLiability.category,
            ownership: 'person1'
          }
        ]
      }));
      setNewLiability({ value: '', category: '', name: '' });
    }
  };

  // Remove asset or liability
  const handleRemoveAsset = (id) => {
    setFormData(prev => ({
      ...prev,
      assets: prev.assets.filter(asset => asset.id !== id)
    }));
  };

  const handleRemoveLiability = (id) => {
    setFormData(prev => ({
      ...prev,
      liabilities: prev.liabilities.filter(liability => liability.id !== id)
    }));
  };

  // Update ownership for asset or liability
  const handleUpdateOwnership = (type, id, ownership) => {
    setFormData(prev => ({
      ...prev,
      [type]: prev[type].map(item => 
        item.id === id ? { ...item, ownership } : item
      )
    }));
  };

  // Add new income or expense
  const handleAddIncome = () => {
    if (newIncome.value && newIncome.category) {
      setFormData(prev => ({
        ...prev,
        income: [
          ...prev.income,
          {
            id: Date.now(),
            name: newIncome.name || incomeCategories.find(cat => cat.value === newIncome.category)?.label || 'Unnamed Income',
            value: newIncome.value,
            category: newIncome.category,
            ownership: 'person1'
          }
        ]
      }));
      setNewIncome({ value: '', category: '', name: '' });
    }
  };

  const handleAddExpense = () => {
    if (newExpense.value && newExpense.category) {
      setFormData(prev => ({
        ...prev,
        expenses: [
          ...prev.expenses,
          {
            id: Date.now(),
            name: newExpense.name || expenseCategories.find(cat => cat.value === newExpense.category)?.label || 'Unnamed Expense',
            value: newExpense.value,
            category: newExpense.category,
            ownership: 'person1'
          }
        ]
      }));
      setNewExpense({ value: '', category: '', name: '' });
    }
  };

  // Remove income or expense
  const handleRemoveIncome = (id) => {
    setFormData(prev => ({
      ...prev,
      income: prev.income.filter(income => income.id !== id)
    }));
  };

  const handleRemoveExpense = (id) => {
    setFormData(prev => ({
      ...prev,
      expenses: prev.expenses.filter(expense => expense.id !== id)
    }));
  };

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      await updateUserProfile({
        ...formData,
        onboarding_completed: true,
      });

      onComplete();
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>Your Information</Typography>
            <TextField
              fullWidth
              label="First Name"
              value={formData.firstName}
              onChange={(e) => handleInputChange(null, 'firstName', e.target.value)}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Last Name"
              value={formData.lastName}
              onChange={(e) => handleInputChange(null, 'lastName', e.target.value)}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Date of Birth"
              type="date"
              value={formData.dateOfBirth}
              onChange={(e) => handleInputChange(null, 'dateOfBirth', e.target.value)}
              margin="normal"
              required
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        );

      case 1:
        return (
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={hasPartner}
                  onChange={handlePartnerToggle}
                />
              }
              label="I have a partner"
            />
            
            {hasPartner && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>Partner Information</Typography>
                <TextField
                  fullWidth
                  label="Partner's First Name"
                  value={formData.partner.firstName}
                  onChange={(e) => handleInputChange('partner', 'firstName', e.target.value)}
                  margin="normal"
                  required
                />
                <TextField
                  fullWidth
                  label="Partner's Last Name"
                  value={formData.partner.lastName}
                  onChange={(e) => handleInputChange('partner', 'lastName', e.target.value)}
                  margin="normal"
                  required
                />
                <TextField
                  fullWidth
                  label="Partner's Date of Birth"
                  type="date"
                  value={formData.partner.dateOfBirth}
                  onChange={(e) => handleInputChange('partner', 'dateOfBirth', e.target.value)}
                  margin="normal"
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            )}
          </Box>
        );

      case 2:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>Retirement Goals</Typography>
            <TextField
              fullWidth
              label="Your Retirement Age"
              type="number"
              value={formData.retirementAge}
              onChange={(e) => handleInputChange(null, 'retirementAge', e.target.value)}
              margin="normal"
              required
              InputProps={{ inputProps: { min: 0 } }}
            />
            {hasPartner && (
              <TextField
                fullWidth
                label="Partner's Retirement Age"
                type="number"
                value={formData.partnerRetirementAge}
                onChange={(e) => handleInputChange(null, 'partnerRetirementAge', e.target.value)}
                margin="normal"
                required
                InputProps={{ inputProps: { min: 0 } }}
              />
            )}
            <TextField
              fullWidth
              label="Annual Retirement Living Expenses"
              type="number"
              value={formData.retirementLivingExpenses}
              onChange={(e) => handleInputChange(null, 'retirementLivingExpenses', e.target.value)}
              margin="normal"
              required
              InputProps={{
                startAdornment: '$',
                inputProps: { min: 0 }
              }}
            />
          </Box>
        );

      case 3:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>Assets</Typography>
            
            {/* Add new asset */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField
                label="Name"
                value={newAsset.name}
                onChange={(e) => setNewAsset({ ...newAsset, name: e.target.value })}
                sx={{ flexGrow: 2 }}
                placeholder="e.g., Emergency Fund, 401(k)"
              />
              <TextField
                label="Amount"
                type="number"
                value={newAsset.value}
                onChange={(e) => setNewAsset({ ...newAsset, value: e.target.value })}
                sx={{ flexGrow: 1 }}
                InputProps={{ inputProps: { min: 0 } }}
              />
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={newAsset.category}
                  onChange={(e) => setNewAsset({ ...newAsset, category: e.target.value })}
                  label="Category"
                >
                  {assetCategories.map(category => (
                    <MenuItem key={category.value} value={category.value}>
                      {category.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button 
                variant="contained" 
                onClick={handleAddAsset}
                disabled={!newAsset.value || !newAsset.category}
              >
                Add
              </Button>
            </Box>

            {/* List of assets */}
            {formData.assets.length > 0 ? (
              <Box sx={{ mb: 4 }}>
                {formData.assets.map(asset => (
                  <Box 
                    key={asset.id} 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,  // Reduced gap
                      mb: 2,
                      width: '100%',
                      pr: 1 // Add right padding
                    }}
                  >
                    <Typography sx={{ width: '30%', minWidth: 120 }} noWrap>
                      {asset.name}
                    </Typography>
                    <Typography sx={{ width: '20%', minWidth: 100 }} noWrap>
                      {assetCategories.find(cat => cat.value === asset.category)?.label || 'Unknown'}
                    </Typography>
                    <Typography sx={{ width: '20%', minWidth: 80 }} noWrap>
                      ${parseFloat(asset.value).toLocaleString()}
                    </Typography>
                    {hasPartner && (
                      <FormControl sx={{ width: '20%', minWidth: 120 }}>
                        <Select
                          value={asset.ownership || 'person1'}
                          onChange={(e) => handleUpdateOwnership('assets', asset.id, e.target.value)}
                          size="small"
                        >
                          <MenuItem value="person1">{formData.firstName || 'Your Name'}</MenuItem>
                          <MenuItem value="partner">{formData.partner.firstName || 'Partner'}</MenuItem>
                          <MenuItem value="joint">Joint</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                    <Button 
                      variant="outlined" 
                      color="error" 
                      size="small"
                      onClick={() => handleRemoveAsset(asset.id)}
                      sx={{ 
                        minWidth: 'auto',
                        ml: 'auto' // Push to the right
                      }}
                    >
                      Remove
                    </Button>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography color="text.secondary" sx={{ mb: 4 }}>
                No assets added yet
              </Typography>
            )}

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>Liabilities</Typography>
            
            {/* Add new liability */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField
                label="Name"
                value={newLiability.name}
                onChange={(e) => setNewLiability({ ...newLiability, name: e.target.value })}
                sx={{ flexGrow: 2 }}
                placeholder="e.g., Home Mortgage, Car Loan"
              />
              <TextField
                label="Amount"
                type="number"
                value={newLiability.value}
                onChange={(e) => setNewLiability({ ...newLiability, value: e.target.value })}
                sx={{ flexGrow: 1 }}
                InputProps={{ inputProps: { min: 0 } }}
              />
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={newLiability.category}
                  onChange={(e) => setNewLiability({ ...newLiability, category: e.target.value })}
                  label="Category"
                >
                  {liabilityCategories.map(category => (
                    <MenuItem key={category.value} value={category.value}>
                      {category.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button 
                variant="contained" 
                onClick={handleAddLiability}
                disabled={!newLiability.value || !newLiability.category}
              >
                Add
              </Button>
            </Box>

            {/* List of liabilities */}
            {formData.liabilities.length > 0 ? (
              <Box>
                {formData.liabilities.map(liability => (
                  <Box 
                    key={liability.id} 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,  // Reduced gap
                      mb: 2,
                      width: '100%',
                      pr: 1 // Add right padding
                    }}
                  >
                    <Typography sx={{ width: '30%', minWidth: 120 }} noWrap>
                      {liability.name}
                    </Typography>
                    <Typography sx={{ width: '20%', minWidth: 100 }} noWrap>
                      {liabilityCategories.find(cat => cat.value === liability.category)?.label || 'Unknown'}
                    </Typography>
                    <Typography sx={{ width: '20%', minWidth: 80 }} noWrap>
                      ${parseFloat(liability.value).toLocaleString()}
                    </Typography>
                    {hasPartner && (
                      <FormControl sx={{ width: '20%', minWidth: 120 }}>
                        <Select
                          value={liability.ownership || 'person1'}
                          onChange={(e) => handleUpdateOwnership('liabilities', liability.id, e.target.value)}
                          size="small"
                        >
                          <MenuItem value="person1">{formData.firstName || 'Your Name'}</MenuItem>
                          <MenuItem value="partner">{formData.partner.firstName || 'Partner'}</MenuItem>
                          <MenuItem value="joint">Joint</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                    <Button 
                      variant="outlined" 
                      color="error" 
                      size="small"
                      onClick={() => handleRemoveLiability(liability.id)}
                      sx={{ 
                        minWidth: 'auto',
                        ml: 'auto' // Push to the right
                      }}
                    >
                      Remove
                    </Button>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography color="text.secondary">
                No liabilities added yet
              </Typography>
            )}
          </Box>
        );

      case 4:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>Income</Typography>
            
            {/* Add new income */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField
                label="Name"
                value={newIncome.name}
                onChange={(e) => setNewIncome({ ...newIncome, name: e.target.value })}
                sx={{ flexGrow: 2 }}
                placeholder="e.g., Primary Job, Side Business"
              />
              <TextField
                label="Amount"
                type="number"
                value={newIncome.value}
                onChange={(e) => setNewIncome({ ...newIncome, value: e.target.value })}
                sx={{ flexGrow: 1 }}
                InputProps={{ inputProps: { min: 0 } }}
              />
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={newIncome.category}
                  onChange={(e) => setNewIncome({ ...newIncome, category: e.target.value })}
                  label="Category"
                >
                  {incomeCategories.map(category => (
                    <MenuItem key={category.value} value={category.value}>
                      {category.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button 
                variant="contained" 
                onClick={handleAddIncome}
                disabled={!newIncome.value || !newIncome.category}
              >
                Add
              </Button>
            </Box>

            {/* List of income */}
            {formData.income.length > 0 ? (
              <Box sx={{ mb: 4 }}>
                {formData.income.map(income => (
                  <Box 
                    key={income.id} 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      mb: 2,
                      width: '100%',
                      pr: 1
                    }}
                  >
                    <Typography sx={{ width: '30%', minWidth: 120 }} noWrap>
                      {income.name}
                    </Typography>
                    <Typography sx={{ width: '20%', minWidth: 100 }} noWrap>
                      {incomeCategories.find(cat => cat.value === income.category)?.label || 'Unknown'}
                    </Typography>
                    <Typography sx={{ width: '20%', minWidth: 80 }} noWrap>
                      ${parseFloat(income.value).toLocaleString()}
                    </Typography>
                    {hasPartner && (
                      <FormControl sx={{ width: '20%', minWidth: 120 }}>
                        <Select
                          value={income.ownership || 'person1'}
                          onChange={(e) => handleUpdateOwnership('income', income.id, e.target.value)}
                          size="small"
                        >
                          <MenuItem value="person1">{formData.firstName || 'Your Name'}</MenuItem>
                          <MenuItem value="partner">{formData.partner.firstName || 'Partner'}</MenuItem>
                          <MenuItem value="joint">Joint</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                    <Button 
                      variant="outlined" 
                      color="error" 
                      size="small"
                      onClick={() => handleRemoveIncome(income.id)}
                      sx={{ 
                        minWidth: 'auto',
                        ml: 'auto'
                      }}
                    >
                      Remove
                    </Button>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography color="text.secondary" sx={{ mb: 4 }}>
                No income added yet
              </Typography>
            )}

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>Expenses</Typography>
            
            {/* Add new expense */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField
                label="Name"
                value={newExpense.name}
                onChange={(e) => setNewExpense({ ...newExpense, name: e.target.value })}
                sx={{ flexGrow: 2 }}
                placeholder="e.g., Rent, Car Payment"
              />
              <TextField
                label="Amount"
                type="number"
                value={newExpense.value}
                onChange={(e) => setNewExpense({ ...newExpense, value: e.target.value })}
                sx={{ flexGrow: 1 }}
                InputProps={{ inputProps: { min: 0 } }}
              />
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={newExpense.category}
                  onChange={(e) => setNewExpense({ ...newExpense, category: e.target.value })}
                  label="Category"
                >
                  {expenseCategories.map(category => (
                    <MenuItem key={category.value} value={category.value}>
                      {category.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button 
                variant="contained" 
                onClick={handleAddExpense}
                disabled={!newExpense.value || !newExpense.category}
              >
                Add
              </Button>
            </Box>

            {/* List of expenses */}
            {formData.expenses.length > 0 ? (
              <Box>
                {formData.expenses.map(expense => (
                  <Box 
                    key={expense.id} 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      mb: 2,
                      width: '100%',
                      pr: 1
                    }}
                  >
                    <Typography sx={{ width: '30%', minWidth: 120 }} noWrap>
                      {expense.name}
                    </Typography>
                    <Typography sx={{ width: '20%', minWidth: 100 }} noWrap>
                      {expenseCategories.find(cat => cat.value === expense.category)?.label || 'Unknown'}
                    </Typography>
                    <Typography sx={{ width: '20%', minWidth: 80 }} noWrap>
                      ${parseFloat(expense.value).toLocaleString()}
                    </Typography>
                    {hasPartner && (
                      <FormControl sx={{ width: '20%', minWidth: 120 }}>
                        <Select
                          value={expense.ownership || 'person1'}
                          onChange={(e) => handleUpdateOwnership('expenses', expense.id, e.target.value)}
                          size="small"
                        >
                          <MenuItem value="person1">{formData.firstName || 'Your Name'}</MenuItem>
                          <MenuItem value="partner">{formData.partner.firstName || 'Partner'}</MenuItem>
                          <MenuItem value="joint">Joint</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                    <Button 
                      variant="outlined" 
                      color="error" 
                      size="small"
                      onClick={() => handleRemoveExpense(expense.id)}
                      sx={{ 
                        minWidth: 'auto',
                        ml: 'auto'
                      }}
                    >
                      Remove
                    </Button>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography color="text.secondary">
                No expenses added yet
              </Typography>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: '100%',
          maxWidth: 600,
          borderRadius: '16px',
        }}
      >
        <Typography variant="h5" component="h1" gutterBottom align="center" sx={{ fontWeight: 600 }}>
          Welcome to Path2Wealth
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 4 }}>
          Let's set up your financial profile
        </Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {renderStepContent(activeStep)}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            onClick={handleBack}
            disabled={activeStep === 0 || loading}
            variant="outlined"
          >
            Back
          </Button>
          <Button
            onClick={activeStep === steps.length - 1 ? handleSubmit : handleNext}
            variant="contained"
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : activeStep === steps.length - 1 ? (
              'Complete Setup'
            ) : (
              'Next'
            )}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default Onboarding; 